import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Printer, FileText, IndianRupee, ArrowRight, Receipt, Trash2 } from "lucide-react";

export default function AccountsDashboard() {
  const { user } = useAuth();
  const canDeleteBill = user && ["super_admin", "admin"].includes(user.role);
  const [unbilled, setUnbilled] = useState([]);
  const [bills, setBills] = useState([]);
  const [sel, setSel] = useState([]);
  const [openBill, setOpenBill] = useState(false);
  const [overview, setOverview] = useState(null);

  const fetchAll = async () => {
    try {
      const [{ data: t }, { data: b }, { data: o }] = await Promise.all([
        api.get("/trips?unbilled=true"),
        api.get("/bills"),
        api.get("/ops/overview"),
      ]);
      setUnbilled(t.trips);
      setBills(b.bills);
      setOverview(o);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { fetchAll(); }, []);

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectedTrips = useMemo(() => unbilled.filter(t => sel.includes(t.id)), [unbilled, sel]);
  const selectedSubtotal = selectedTrips.reduce((a, t) => a + Number(t.money?.customer_freight || 0), 0);

  const deleteBill = async (b) => {
    if (!window.confirm(`Move bill ${b.bill_no} to the recycle bin? Its ${b.trips?.length || 0} trip(s) will become unbilled again. You can restore it within 90 days.`)) return;
    try { await api.delete(`/bills/${b.id}`); toast.success(`Bill ${b.bill_no} moved to recycle bin`); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="overline text-[#FF5A00]">ACCOUNTS · BILLING &amp; PAYMENTS</div>
        <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="accounts-dashboard">Bill trips. Book payments.</h1>

        {overview && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Unbilled deliveries" value={overview.trips.delivered_unbilled} accent />
            <Stat label="Bills pending approval" value={overview.bills.pending} />
            <Stat label="Bills unpaid" value={overview.bills.unpaid} />
            <Stat label="Total revenue (INR)" value={formatINR(overview.revenue_all_time_inr || 0)} />
          </div>
        )}

        <Tabs defaultValue="unbilled" className="mt-10">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="unbilled" data-testid="acc-tab-unbilled" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Unbilled trips ({unbilled.length})</TabsTrigger>
            <TabsTrigger value="bills" data-testid="acc-tab-bills" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Bills ({bills.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="unbilled" className="mt-6">
            {sel.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 p-3 bg-[#FF5A00]/10 border border-[#FF5A00]/40 rounded-sm">
                <div className="text-sm">
                  <span className="text-[#FF5A00] font-semibold">{sel.length} trip(s) selected</span> · Subtotal <span className="font-mono-ll">{formatINR(selectedSubtotal)}</span>
                </div>
                <Dialog open={openBill} onOpenChange={setOpenBill}>
                  <DialogTrigger asChild>
                    <Button data-testid="bulk-bill-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
                      <Receipt className="w-4 h-4 mr-1" /> Bill selected in one go
                    </Button>
                  </DialogTrigger>
                  <BulkBillDialog selectedTrips={selectedTrips} onDone={() => { setOpenBill(false); setSel([]); fetchAll(); }} />
                </Dialog>
              </div>
            )}
            <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3 w-8"></th>
                    <th className="text-left p-3">CN #</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Route</th>
                    <th className="text-left p-3">Freight</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unbilled.map((t) => (
                    <tr key={t.id} data-testid={`unbilled-row-${t.id}`} className="border-t border-[#2A2A2A]">
                      <td className="p-3">
                        <Checkbox checked={sel.includes(t.id)} onCheckedChange={() => toggle(t.id)}
                          data-testid={`unbilled-check-${t.id}`} className="border-[#2A2A2A] data-[state=checked]:bg-[#FF5A00] data-[state=checked]:border-[#FF5A00]" />
                      </td>
                      <td className="p-3 font-mono-ll text-xs">{t.cn_number}</td>
                      <td className="p-3 text-xs">
                        <div>{t.customer?.name}</div>
                        <div className="text-zinc-500">{t.customer?.gstin || "—"}</div>
                      </td>
                      <td className="p-3 font-mono-ll text-xs inline-flex items-center gap-2">
                        <span>{t.source_city}</span><ArrowRight className="w-3 h-3 text-[#FF5A00]" /><span>{t.destination_city}</span>
                      </td>
                      <td className="p-3">{formatINR(t.money?.customer_freight || 0)}</td>
                      <td className="p-3 text-right">
                        <Link to={`/trips/${t.id}`}>
                          <Button size="sm" variant="outline" className="h-8 rounded-sm border-[#2A2A2A] bg-transparent hover:bg-white/5">Open</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {unbilled.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No unbilled trips — everything settled.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="bills" className="mt-6">
            <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">Bill #</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Trips</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3">Paid / Balance</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((b) => (
                    <tr key={b.id} data-testid={`bill-row-${b.id}`} className="border-t border-[#2A2A2A]">
                      <td className="p-3 font-mono-ll text-xs">{b.bill_no}</td>
                      <td className="p-3 text-xs">{b.customer_name}</td>
                      <td className="p-3 text-xs">{b.trips?.length || 0}</td>
                      <td className="p-3">{formatINR(b.total)}</td>
                      <td className="p-3 text-xs">
                        <div>Paid {formatINR(b.amount_paid)}</div>
                        <div className="text-zinc-500">Balance {formatINR(b.balance)}</div>
                      </td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm ${
                          b.status === "pending_approval" ? "bg-[#FFD700]/15 text-[#FFD700]" :
                          b.status === "approved" ? "bg-blue-900/40 text-blue-300" :
                          b.status === "paid" ? "bg-emerald-900/40 text-emerald-300" :
                          b.status === "partial" ? "bg-[#FF5A00]/15 text-[#FF5A00]" : "bg-zinc-800 text-zinc-300"
                        }`}>{b.status.replace("_", " ")}</span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-2">
                          <Link to={`/prints/bill/${b.id}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="h-8 rounded-sm border-[#2A2A2A] hover:bg-[#FF5A00]/10 text-[#FF5A00]"
                              data-testid={`bill-print-${b.id}`}>
                              <Printer className="w-3 h-3 mr-1" /> Print
                            </Button>
                          </Link>
                          {b.status !== "paid" && b.status !== "cancelled" && (
                            <PayDialog bill={b} onDone={fetchAll} />
                          )}
                          {canDeleteBill && (
                            <Button size="sm" variant="outline" onClick={() => deleteBill(b)}
                              data-testid={`bill-delete-${b.id}`}
                              className="h-8 rounded-sm border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10">
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {bills.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-zinc-500">No bills yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-[#2A2A2A] bg-[#141414]"}`}>
      <div className="overline text-zinc-400">{label}</div>
      <div className={`font-display font-black text-2xl mt-2 ${accent ? "text-[#FF5A00]" : ""}`}>{value}</div>
    </div>
  );
}

function BulkBillDialog({ selectedTrips, onDone }) {
  const first = selectedTrips[0]?.customer || {};
  const [customer_name, setName] = useState(first.name || "");
  const [customer_gstin, setGst] = useState(first.gstin || "");
  const [customer_address, setAddr] = useState("");
  const [tax_percent, setTax] = useState(18);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const subtotal = selectedTrips.reduce((a, t) => a + Number(t.money?.customer_freight || 0), 0);
  const tax = subtotal * tax_percent / 100;
  const total = subtotal + tax;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/bills", {
        customer_name, customer_gstin, customer_address, tax_percent: Number(tax_percent),
        trip_ids: selectedTrips.map(t => t.id), notes,
      });
      toast.success(`Bill created · ${data.bill.bill_no}`);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A] max-w-lg">
      <DialogHeader><DialogTitle className="font-display font-black">Generate consolidated bill</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="bulk-bill-form">
        <div>
          <Label className="overline text-zinc-400">Customer name</Label>
          <Input required value={customer_name} onChange={(e) => setName(e.target.value)}
            data-testid="bill-customer-name" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="overline text-zinc-400">GSTIN</Label>
            <Input value={customer_gstin} onChange={(e) => setGst(e.target.value)}
              data-testid="bill-customer-gstin" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
          </div>
          <div>
            <Label className="overline text-zinc-400">Tax %</Label>
            <Input type="number" value={tax_percent} onChange={(e) => setTax(Number(e.target.value))}
              data-testid="bill-tax-percent" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
          </div>
        </div>
        <div>
          <Label className="overline text-zinc-400">Customer address</Label>
          <Input value={customer_address} onChange={(e) => setAddr(e.target.value)}
            data-testid="bill-customer-address" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
        </div>
        <div>
          <Label className="overline text-zinc-400">Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)}
            className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
        </div>
        <div className="p-3 bg-[#141414] border border-[#2A2A2A] text-sm">
          <div className="flex justify-between"><span className="text-zinc-400">Subtotal ({selectedTrips.length} trips)</span><span>{formatINR(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Tax @ {tax_percent}%</span><span>{formatINR(tax)}</span></div>
          <div className="flex justify-between mt-1 pt-1 border-t border-[#2A2A2A] font-display font-bold text-lg text-[#FF5A00]"><span>Total</span><span>{formatINR(total)}</span></div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} data-testid="bulk-bill-submit"
            className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-11 px-6">
            {busy ? "Creating..." : "Create Bill"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function PayDialog({ bill, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmt] = useState(bill.balance || 0);
  const [method, setMethod] = useState("bank");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/bills/${bill.id}/payment`, { amount: Number(amount), method, note });
      toast.success("Payment recorded");
      setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`bill-pay-${bill.id}`} className="h-8 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
          <IndianRupee className="w-3 h-3 mr-1" /> Payment
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A]">
        <DialogHeader><DialogTitle className="font-display font-black">Record payment · {bill.bill_no}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="overline text-zinc-400">Amount (₹)</Label>
            <Input type="number" required value={amount} onChange={(e) => setAmt(e.target.value)}
              data-testid="bill-pay-amount" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
          </div>
          <div>
            <Label className="overline text-zinc-400">Method</Label>
            <Input value={method} onChange={(e) => setMethod(e.target.value)}
              className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
          </div>
          <div>
            <Label className="overline text-zinc-400">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="bill-pay-submit"
              className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-11 px-6">
              {busy ? "Saving..." : "Record payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
